import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Lock, CheckCircle2, ChevronDown, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import VideoPlaceholder from "@/components/VideoPlaceholder";
import { useParticipant } from "@/context/ParticipantContext";
import { TRAINING_MODULES, TOTAL_MODULES } from "@/data/trainingContent";
import { TRAINING } from "@/constants/testIds";

export default function TrainingManual() {
  const { progress, refreshProgress, markModuleComplete } = useParticipant();
  const [openModule, setOpenModule] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    refreshProgress();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const completedIds = progress?.modules_completed || [];
  const completedCount = completedIds.length;
  const percentage = Math.round((completedCount / TOTAL_MODULES) * 100);

  const isUnlocked = (module) => {
    if (module.order === 1) return true;
    const prevModule = TRAINING_MODULES[module.order - 2];
    return completedIds.includes(prevModule.id);
  };

  const handleComplete = async (moduleId) => {
    await markModuleComplete(moduleId);
  };

  const allDone = completedCount >= TOTAL_MODULES;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 space-y-8">
      <div className="space-y-3">
        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900">Training Manual</h1>
        <p className="text-slate-600 text-base">
          Watch each module in order. The next module unlocks once you complete the current one.
        </p>
        <div className="flex items-center gap-3">
          <Progress value={percentage} data-testid={TRAINING.progressBar} className="h-2 flex-1" />
          <span className="text-sm font-semibold text-slate-700 whitespace-nowrap">
            {completedCount}/{TOTAL_MODULES} completed
          </span>
        </div>
      </div>

      <div className="space-y-3">
        {TRAINING_MODULES.map((module) => {
          const unlocked = isUnlocked(module);
          const completed = completedIds.includes(module.id);
          const isOpen = openModule === module.id;

          return (
            <motion.div
              key={module.id}
              data-testid={TRAINING.moduleCard(module.id)}
              className={`bg-white rounded-md border transition-all duration-200 ${
                unlocked ? "border-slate-200" : "border-slate-100 opacity-60"
              }`}
            >
              <button
                onClick={() => unlocked && setOpenModule(isOpen ? null : module.id)}
                disabled={!unlocked}
                className={`w-full flex items-center justify-between p-5 text-left ${
                  unlocked ? "cursor-pointer" : "cursor-not-allowed"
                }`}
              >
                <div className="flex items-center gap-4">
                  <div
                    className={`h-9 w-9 rounded-full flex items-center justify-center text-sm font-bold ${
                      completed
                        ? "bg-emerald-100 text-emerald-600"
                        : unlocked
                        ? "bg-red-50 text-denso-red"
                        : "bg-slate-100 text-slate-400"
                    }`}
                  >
                    {completed ? <CheckCircle2 className="h-5 w-5" /> : module.order}
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800">{module.title}</h3>
                    <p className="text-xs text-slate-500">{module.subtitle}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {completed && (
                    <span
                      data-testid={TRAINING.completedBadge(module.id)}
                      className="text-xs font-semibold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full"
                    >
                      Watched
                    </span>
                  )}
                  {!unlocked && (
                    <span
                      data-testid={TRAINING.moduleLockedBadge(module.id)}
                      className="flex items-center gap-1 text-xs font-semibold text-slate-400"
                    >
                      <Lock className="h-3.5 w-3.5" /> Locked
                    </span>
                  )}
                  {unlocked && (
                    <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                  )}
                </div>
              </button>

              <AnimatePresence>
                {isOpen && unlocked && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    className="overflow-hidden"
                  >
                    <div className="px-5 pb-5 space-y-4 border-t border-slate-100 pt-4">
                      <VideoPlaceholder
                        moduleId={module.id}
                        title={module.title}
                        videoUrl={module.videoUrl}
                        completed={completed}
                        onComplete={() => handleComplete(module.id)}
                        testIdPrefix="training"
                      />
                      <ul className="space-y-2">
                        {module.keyPoints.map((point, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-slate-600 leading-relaxed">
                            <span className="h-1.5 w-1.5 rounded-full bg-denso-red mt-1.5 shrink-0" />
                            {point}
                          </li>
                        ))}
                      </ul>
                      {!completed && (
                        <Button
                          data-testid={TRAINING.markCompleteButton(module.id)}
                          variant="outline"
                          onClick={() => handleComplete(module.id)}
                          className="text-denso-red border-denso-red/30 hover:bg-red-50"
                        >
                          {module.videoUrl ? "Mark Video as Watched" : "Mark Module as Watched (placeholder)"}
                        </Button>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </div>

      {allDone && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-emerald-50 border border-emerald-200 rounded-md p-6 text-center space-y-3"
        >
          <CheckCircle2 className="h-8 w-8 text-emerald-600 mx-auto" />
          <p className="font-semibold text-emerald-800">
            All training modules completed! You can now take the Q&A assessment.
          </p>
          <Button
            data-testid={TRAINING.continueToQuizButton}
            onClick={() => navigate("/quiz")}
            className="bg-denso-red hover:bg-denso-lightred text-white"
          >
            Continue to Q&A <ArrowRight className="h-4 w-4" />
          </Button>
        </motion.div>
      )}
    </div>
  );
}
